/**
 * React Query Hooks for Cohorts API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cohortsApi, CreateCohortRequest, UpdateCohortRequest } from '../../api/cohorts';
import { queryKeys } from '../../lib/queryClient';

/**
 * Fetch cohorts for a program
 */
export function useCohorts(programId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cohorts.list(programId),
    queryFn: () => cohortsApi.list(programId!),
    enabled: !!programId,
  });
}

/**
 * Fetch single cohort by ID
 */
export function useCohort(cohortId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cohorts.detail(cohortId!),
    queryFn: () => cohortsApi.get(cohortId!),
    enabled: !!cohortId,
  });
}

/**
 * Fetch enrollments for a cohort
 */
export function useCohortEnrollments(cohortId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cohorts.enrollments(cohortId!),
    queryFn: () => cohortsApi.listEnrollments(cohortId!),
    enabled: !!cohortId,
  });
}

/**
 * Fetch cohort participants (detailed view with mentor relationships)
 */
export function useCohortParticipants(cohortId: string | undefined, statusFilter?: string) {
  return useQuery({
    queryKey: queryKeys.cohorts.participants(cohortId!, statusFilter),
    queryFn: () => cohortsApi.getParticipants(cohortId!, statusFilter),
    enabled: !!cohortId,
  });
}

/**
 * Create new cohort
 */
export function useCreateCohort() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateCohortRequest) => cohortsApi.create(data),
    onSuccess: (newCohort) => {
      // Invalidate cohorts list for this program
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.list(newCohort.programId) });
    },
  });
}

/**
 * Update existing cohort
 */
export function useUpdateCohort() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ cohortId, data }: { cohortId: string; data: UpdateCohortRequest }) =>
      cohortsApi.update(cohortId, data),
    onSuccess: (updatedCohort, variables) => {
      // Invalidate specific cohort and list
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.list(updatedCohort.programId) });
    },
  });
}

/**
 * Delete cohort
 */
export function useDeleteCohort() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ cohortId, programId }: { cohortId: string; programId: string }) =>
      cohortsApi.delete(cohortId, programId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.list(variables.programId) });
    },
  });
}

/**
 * Enroll user in cohort
 */
export function useEnrollUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ cohortId, userId, programId }: { cohortId: string; userId: string; programId: string }) =>
      cohortsApi.enrollUser(cohortId, userId, programId),
    onSuccess: (_, variables) => {
      // Invalidate queries - React Query will refetch when needed
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(variables.cohortId) });
    },
  });
}

/**
 * Unenroll user from cohort (soft withdraw)
 */
export function useUnenrollUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ cohortId, userId }: { cohortId: string; userId: string }) =>
      cohortsApi.unenrollUser(cohortId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(variables.cohortId) });
    },
  });
}

/**
 * Hard-delete a single enrollment record (admin only)
 */
export function useDeleteEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cohortId, userId }: { cohortId: string; userId: string }) =>
      cohortsApi.deleteEnrollment(cohortId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(variables.cohortId) });
    },
  });
}

/**
 * Hard-delete multiple enrollment records (admin only)
 */
export function useBulkDeleteEnrollments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cohortId, userIds }: { cohortId: string; userIds: string[] }) =>
      cohortsApi.bulkDeleteEnrollments(cohortId, userIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(variables.cohortId) });
    },
  });
}

/**
 * Bulk enroll users in cohort
 */
export function useBulkEnroll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cohortId, users }: { cohortId: string; users: { userId: string; userName: string; userEmail: string }[] }) =>
      cohortsApi.bulkEnroll(cohortId, users),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(variables.cohortId) });
    },
  });
}

/**
 * Publish a cohort (one-way, idempotent)
 */
export function usePublishCohort() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cohortId: string) => cohortsApi.publish(cohortId),
    onSuccess: (_, cohortId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.lists() });
    },
  });
}
